/**
 * Alert Preferences API Service
 *
 * Per Plan: docs/USER_DEFINED_ALERTS_PLAN.md
 *
 * Provides API communication for:
 * - User alert preferences (topics, severity, timing, delivery)
 * - Custom alert rules (threshold-based alerts)
 * - Available metrics and operators
 */

import api from '../config/api';

export const alertPreferencesService = {
  // ============================================
  // User Preferences
  // ============================================

  /**
   * Get current user's alert preferences
   */
  getPreferences: async () => {
    const response = await api.get('/api/alert-preferences');
    return response.data;
  },

  /**
   * Update user's alert preferences
   * @param {Object} preferences - Preferences object
   */
  updatePreferences: async (preferences) => {
    const response = await api.put('/api/alert-preferences', preferences);
    return response.data;
  },

  /**
   * Send a test alert to verify delivery settings
   */
  sendTestAlert: async () => {
    const response = await api.post('/api/alert-preferences/test');
    return response.data;
  },

  /**
   * Preview how current preferences would filter alerts
   */
  previewFiltering: async () => {
    const response = await api.get('/api/alert-preferences/preview');
    return response.data;
  },

  // ============================================
  // Custom Alert Rules
  // ============================================

  /**
   * Get all custom rules for the user
   */
  getRules: async () => {
    const response = await api.get('/api/alert-rules');
    return response.data;
  },

  /**
   * Create a new custom rule
   * @param {Object} rule - Rule definition
   */
  createRule: async (rule) => {
    const response = await api.post('/api/alert-rules', rule);
    return response.data;
  },

  /**
   * Update an existing rule
   * @param {string} ruleId - Rule ID
   * @param {Object} rule - Updated rule definition
   */
  updateRule: async (ruleId, rule) => {
    const response = await api.put(`/api/alert-rules/${ruleId}`, rule);
    return response.data;
  },

  /**
   * Delete a rule
   * @param {string} ruleId - Rule ID to delete
   */
  deleteRule: async (ruleId) => {
    const response = await api.delete(`/api/alert-rules/${ruleId}`);
    return response.data;
  },

  /**
   * Test a rule against current data
   * @param {string} ruleId - Rule ID to test
   */
  testRule: async (ruleId) => {
    const response = await api.post(`/api/alert-rules/${ruleId}/test`);
    return response.data;
  },

  /**
   * Get available metrics for rule creation
   */
  getAvailableMetrics: async () => {
    const response = await api.get('/api/alert-rules/metrics');
    return response.data;
  },

  // ============================================
  // Proactive Schedule Management
  // ============================================

  /**
   * Get proactive schedules for the user's company
   */
  getSchedules: async () => {
    const response = await api.get('/api/alert-preferences/schedules');
    return response.data;
  },

  /**
   * Update a proactive schedule (cron expression, enabled status)
   * @param {string} scheduleId - Schedule ID
   * @param {Object} updates - { cron_expression, is_enabled }
   */
  updateSchedule: async (scheduleId, updates) => {
    const response = await api.put(`/api/alert-preferences/schedules/${scheduleId}`, updates);
    return response.data;
  },

  // ============================================
  // Alert Notifications (for notification bell)
  // ============================================

  /**
   * Get user's alerts (filtered by preferences)
   * @param {Object} options - Query options
   * @param {number} options.limit - Max alerts to return (default: 20)
   * @param {number} options.offset - Pagination offset (default: 0)
   * @param {boolean} options.unread_only - Only return unread alerts
   */
  getAlerts: async (options = {}) => {
    const { limit = 20, offset = 0, unread_only = false } = options;
    const response = await api.get('/api/alert-preferences/alerts', {
      params: { limit, offset, unread_only }
    });
    return response.data;
  },

  /**
   * Get unread alert count (for notification badge)
   */
  getUnreadCount: async () => {
    const response = await api.get('/api/alert-preferences/alerts/unread-count');
    return response.data;
  },

  /**
   * Mark specific alerts as read
   * @param {string[]} alertIds - Array of alert IDs to mark as read
   */
  markAlertsRead: async (alertIds) => {
    const response = await api.patch('/api/alert-preferences/alerts/read', {
      alert_ids: alertIds
    });
    return response.data;
  },

  /**
   * Mark all alerts as read
   */
  markAllAlertsRead: async () => {
    const response = await api.patch('/api/alert-preferences/alerts/read-all');
    return response.data;
  },

  /**
   * Dismiss an alert
   * @param {string} alertId - Alert ID to dismiss
   */
  dismissAlert: async (alertId) => {
    const response = await api.patch(`/api/alert-preferences/alerts/${alertId}/dismiss`);
    return response.data;
  },
};

export default alertPreferencesService;
